"use client";

/**
 * useSocialActivity - Hook for fetching social activity on markets
 *
 * Fetches which friends, tracked wallets, and copytrading leaders
 * have traded on specified markets.
 *
 * Caching strategy (fast perceived loading):
 * 1. sessionStorage: instant restore on page load (persists across navigations)
 * 2. In-memory cache: avoid re-fetches within 2 minutes
 * 3. Background fetch: updates both caches silently
 */

import { useState, useEffect, useMemo } from "react";
import { MarketSocialActivity } from "@/app/components/SocialActivityIndicator";
import { fetchWithRetry } from "@/lib/fetchWithRetry";

interface UseSocialActivityResult {
  activity: Record<string, MarketSocialActivity>;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// In-memory cache (fastest, lost on page refresh)
const activityCache = new Map<string, { data: Record<string, MarketSocialActivity>; timestamp: number }>();
const CACHE_TTL = 120000; // 2 minutes

// Per-slug in-flight registry — prevents the race where slugs trickle into
// `marketSlugs` over 9-10 ticks and each tick's effect re-runs against a
// cache that hasn't been populated yet (the previous fetch is still in
// flight). Without this, the slug list "A,B,C" → "A,B,C,D" → "A,B,C,D,E"
// chain fired one POST per added slug instead of debouncing into a single
// batch. Resolved with the slug's row from the eventual response, or null
// when the row is absent / the fetch fails — either way, downstream
// components dedupe through this layer rather than against the cache.
const inflightSlugFetches = new Map<string, Promise<MarketSocialActivity | null>>();
// Lowered from 50 → 25 to keep the backend SQL query cheap. Each batch
// becomes a `ANY(${slugs})` lookup against positions / tracker_positions
// / polymarket_positions; with the 700ms debounce above, ~30-50 slugs
// were landing per request and the 3 parallel queries occasionally hit
// the upstream 30s timeout, surfacing as a 502 to the client. 25 slugs
// halves that cost.
const BATCH_SIZE = 25;
const SESSION_STORAGE_KEY = "frenflow_social_activity";
const SESSION_CACHE_TTL = 300000; // 5 minutes for sessionStorage

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/** Read all cached social activity from sessionStorage */
function getSessionCache(): Record<string, MarketSocialActivity> {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Expired?
    if (parsed.timestamp && Date.now() - parsed.timestamp > SESSION_CACHE_TTL) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return {};
    }
    return parsed.data || {};
  } catch { return {}; }
}

/** Write social activity to sessionStorage (merge with existing) */
function updateSessionCache(newData: Record<string, MarketSocialActivity>) {
  if (typeof window === "undefined") return;
  try {
    const existing = getSessionCache();
    const merged = { ...existing, ...newData };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
      data: merged,
      timestamp: Date.now(),
    }));
  } catch { /* quota exceeded — ignore */ }
}

export function useSocialActivity(marketSlugs: string[]): UseSocialActivityResult {
  // Initialize from sessionStorage for instant display
  const [activity, setActivity] = useState<Record<string, MarketSocialActivity>>(() => {
    const cached = getSessionCache();
    // Only return entries for requested slugs
    const result: Record<string, MarketSocialActivity> = {};
    for (const slug of marketSlugs) {
      if (cached[slug]) result[slug] = cached[slug];
    }
    return result;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  // Stable slugs key to avoid unnecessary re-fetches
  const slugsKey = marketSlugs.filter(Boolean).join(",");

  useEffect(() => {
    const normalizedSlugs = [...new Set(slugsKey.split(",").filter(Boolean).map(s => s.trim()))];

    if (normalizedSlugs.length === 0) {
      setActivity({});
      setIsLoading(false);
      return;
    }

    // Filter to only slugs we DON'T have cached yet (per-slug granular cache)
    const now = Date.now();
    const uncachedSlugs: string[] = [];
    const cachedResults: Record<string, MarketSocialActivity> = {};

    // Check in-memory cache per slug
    for (const slug of normalizedSlugs) {
      const cached = activityCache.get(slug);
      if (cached && now - cached.timestamp < CACHE_TTL) {
        cachedResults[slug] = cached.data[slug] || cached.data;
      } else {
        uncachedSlugs.push(slug);
      }
    }

    // Pre-populate from sessionStorage for uncached slugs
    const sessionData = getSessionCache();
    for (const slug of uncachedSlugs) {
      if (sessionData[slug]) {
        cachedResults[slug] = sessionData[slug];
      }
    }

    // Show cached data immediately
    if (Object.keys(cachedResults).length > 0) {
      setActivity(prev => ({ ...prev, ...cachedResults }));
    }

    // Split between slugs we need to fetch fresh and slugs already in
    // flight from a previous render's effect. The in-flight ones get
    // awaited (no extra request); only fresh slugs go into a chunked POST.
    const stillToFetch: string[] = [];
    const inflightWaits: Promise<{ slug: string; data: MarketSocialActivity | null }>[] = [];
    for (const slug of uncachedSlugs) {
      if (sessionData[slug]) continue;
      const existing = inflightSlugFetches.get(slug);
      if (existing) {
        inflightWaits.push(existing.then((data) => ({ slug, data })));
      } else {
        stillToFetch.push(slug);
      }
    }

    if (stillToFetch.length === 0 && inflightWaits.length === 0) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    // Debounce: wait 700ms for more slugs to accumulate before fetching.
    // 150ms was too short — on the markets list the slug list grows in
    // 9-10 ticks over ~500ms as SSR, prices, sparklines and forYou
    // separately settle into `events`. Each tick fired its own POST
    // batch even though most slugs overlapped. 700ms catches the whole
    // cascade and collapses it into a single batched request.
    const debounceTimer = setTimeout(async () => {
      if (cancelled) return;
      setIsLoading(true);
      setError(null);

      try {
        const chunks = chunkArray(stillToFetch, BATCH_SIZE);
        const activityData: Record<string, MarketSocialActivity> = {};

        // Reserve every slug as in-flight BEFORE awaiting the fetch.
        // Concurrent effects in other components (or a later tick of
        // this one) see the registered promise and queue up behind it
        // instead of disparando otra POST con los mismos slugs.
        const chunkResolvers: Array<{
          chunk: string[];
          resolvers: Map<string, (data: MarketSocialActivity | null) => void>;
        }> = [];
        for (const chunk of chunks) {
          const resolvers = new Map<string, (data: MarketSocialActivity | null) => void>();
          for (const slug of chunk) {
            const promise = new Promise<MarketSocialActivity | null>((res) => {
              resolvers.set(slug, res);
            });
            inflightSlugFetches.set(slug, promise);
          }
          chunkResolvers.push({ chunk, resolvers });
        }

        const settleChunk = (
          resolvers: Map<string, (data: MarketSocialActivity | null) => void>,
          activity: Record<string, MarketSocialActivity>,
        ) => {
          for (const [slug, resolve] of resolvers) {
            resolve(activity[slug] || null);
            // Drop the registry entry — a concurrent effect that's still
            // awaiting holds the resolved Promise reference directly and
            // is unaffected by the Map mutation.
            inflightSlugFetches.delete(slug);
          }
        };

        // Fetch all chunks in parallel (was sequential — N+1 bottleneck)
        const fetchResults = await Promise.all(
          chunkResolvers.map(async ({ chunk, resolvers }) => {
            if (cancelled) {
              settleChunk(resolvers, {});
              return {};
            }
            try {
              const res = await fetchWithRetry('/api/social/markets/activity', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ slugs: chunk }),
              });
              if (res.status === 429) {
                settleChunk(resolvers, {});
                return {};
              }
              if (!res.ok) throw new Error(`Failed to fetch social activity: ${res.status}`);
              const result = await res.json();
              const activity = (result.activity || {}) as Record<string, MarketSocialActivity>;
              settleChunk(resolvers, activity);
              return activity;
            } catch (err) {
              settleChunk(resolvers, {});
              throw err;
            }
          })
        );
        for (const result of fetchResults) {
          Object.assign(activityData, result);
        }

        // Pull anything that came from a previously-inflight fetch (other
        // mount of this hook) so its row also lands in our local state.
        const inflightResolved = await Promise.all(inflightWaits);
        for (const { slug, data } of inflightResolved) {
          if (data) activityData[slug] = data;
        }

        if (cancelled) return;

        // Update per-slug in-memory cache
        for (const [slug, data] of Object.entries(activityData)) {
          activityCache.set(slug, { data: { [slug]: data }, timestamp: Date.now() });
        }

        // Update sessionStorage
        updateSessionCache(activityData);

        setActivity(prev => ({ ...prev, ...activityData }));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to fetch social activity");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }, 700);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugsKey, fetchCount]);

  const refetch = () => setFetchCount(c => c + 1);

  return {
    activity,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Get social activity for a single market
 * Convenience wrapper for components that only need one market
 */
export function useSingleMarketSocialActivity(
  marketSlug: string | undefined | null
): {
  activity: MarketSocialActivity | null;
  isLoading: boolean;
  error: string | null;
} {
  const slugs = useMemo(() => (marketSlug ? [marketSlug] : []), [marketSlug]);
  const { activity, isLoading, error } = useSocialActivity(slugs);

  return {
    activity: marketSlug ? activity[marketSlug] || null : null,
    isLoading,
    error,
  };
}
